import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  AutoAwesome as AiIcon,
  Lightbulb as HintIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import api, { getPastPaperApiPrefix, getPastPaperPortalHomePath, getPastPaperResultPath } from "../../../api";
import HtmlWithLatex from "../../QuestionBank/components/HtmlWithLatex";
import VideoExplanationPlayer from "../../../components/VideoExplanationPlayer";
import {
  stripAnswerBrackets,
  singleValueMatches,
  isAnswerCorrect,
  isAnswerPartial,
  decodeHtmlEntities,
} from "../../../utils/answerMatchUtils";
import QuestionPaperLayout from "../../QuestionBank/components/QuestionPaperLayout";
import ImageOverlayFillInput from "../components/ImageOverlayFillInput";
import InteractiveDiagramCanvas from "../components/InteractiveDiagramCanvas";
import { interactiveRevealValue } from "../utils/interactiveRevealValue";
import {
  coerceFillAnswersArray,
  parsePipeJoinedBlanks,
  isFillQuestionType,
} from "../../../utils/fillQuestionUtils";
import { extractSubCore } from "../utils/groupedQuestionUtils";
import MarksBreakdownTable from "../components/MarksBreakdownTable";

const STUDENT_BASE = process.env.REACT_APP_STUDENT_PREFIX || "student";

const PERFORMANCE_LEVEL_CONFIG = {
  excellent: { label: "Excellent", bgcolor: "#dcfce7", color: "#166534" },
  good: { label: "Good", bgcolor: "#dbeafe", color: "#1e40af" },
  average: { label: "Average", bgcolor: "#fef9c3", color: "#854d0e" },
  needs_improvement: { label: "Needs Improvement", bgcolor: "#fee2e2", color: "#991b1b" },
  unknown: { label: "Unknown", bgcolor: "#f3f4f6", color: "#374151" },
};

const AiInsightsTab = ({ aiFeedbackStatus, aiOverall, aiScore, aiPercentage, totalMarks, ruleScore }) => {
  if (aiFeedbackStatus === "pending" || aiFeedbackStatus === "processing") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 6, gap: 2 }}>
        <CircularProgress size={40} sx={{ color: "#7c3aed" }} />
        <Typography variant="body1" sx={{ color: "#7c3aed", fontWeight: 500 }}>
          AI is analysing your answers…
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This usually takes 5–15 seconds. The page will update automatically.
        </Typography>
      </Box>
    );
  }

  if (aiFeedbackStatus === "failed" || !aiOverall) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        AI feedback is not available for this attempt. This may be due to a temporary issue.
      </Alert>
    );
  }

  const perfConfig = PERFORMANCE_LEVEL_CONFIG[aiOverall.performance_level] ?? PERFORMANCE_LEVEL_CONFIG.unknown;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <Card variant="outlined" sx={{ borderColor: "#e0d7ff", bgcolor: "#f3f0ff" }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <AiIcon sx={{ color: "#7c3aed", fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#7c3aed" }}>
                AI-Adjusted Score
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: "#7c3aed", mb: 0.5 }}>
              {Number(aiScore ?? 0).toFixed(1)}
              <Typography component="span" variant="h6" sx={{ color: "text.secondary", fontWeight: 400 }}>
                {" "}/ {Number(totalMarks ?? 0).toFixed(1)}
              </Typography>
            </Typography>
            <Typography variant="body2" sx={{ color: "#7c3aed", fontWeight: 600 }}>
              {Number(aiPercentage ?? 0).toFixed(1)}%
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              Based on semantic answer matching
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <TrendingUpIcon sx={{ color: "text.secondary", fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Performance Level
              </Typography>
            </Box>
            <Chip
              label={perfConfig.label}
              sx={{ bgcolor: perfConfig.bgcolor, color: perfConfig.color, fontWeight: 700, fontSize: "1rem", height: 36, px: 1, mb: 1 }}
            />
            {ruleScore != null && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                Auto Score: {Number(ruleScore).toFixed(1)} / {Number(totalMarks ?? 0).toFixed(1)}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        {aiOverall.strengths?.length > 0 && (
          <Card variant="outlined" sx={{ borderColor: "#bbf7d0" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <CheckCircleIcon sx={{ color: "#16a34a", fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#166534" }}>
                  Strengths
                </Typography>
              </Box>
              {aiOverall.strengths.map((s, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.75 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#16a34a", mt: 0.7, flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{s}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}
        {aiOverall.weaknesses?.length > 0 && (
          <Card variant="outlined" sx={{ borderColor: "#fecaca" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <CancelIcon sx={{ color: "#dc2626", fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#991b1b" }}>
                  Areas to Improve
                </Typography>
              </Box>
              {aiOverall.weaknesses.map((w, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.75 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#dc2626", mt: 0.7, flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{w}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}
      </Box>

      {aiOverall.recommendations?.length > 0 && (
        <Card variant="outlined" sx={{ borderColor: "#bfdbfe" }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <HintIcon sx={{ color: "#2563eb", fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e40af" }}>
                Study Recommendations
              </Typography>
            </Box>
            {aiOverall.recommendations.map((r, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1 }}>
                <Box sx={{ width: 22, height: 22, borderRadius: "50%", bgcolor: "#dbeafe", color: "#1e40af", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", fontWeight: 700, flexShrink: 0, mt: 0.1 }}>
                  {i + 1}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{r}</Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

// ── AI Feedback Panel ─────────────────────────────────────────────────────────
const AiFeedbackPanel = ({ questionId, aiFeedbackMap, aiFeedbackStatus, questionMarks, ruleIsCorrect, ruleIsPartial }) => {
  const aiq = aiFeedbackMap?.[String(questionId)];

  if (aiFeedbackStatus === "pending" || aiFeedbackStatus === "processing") {
    return (
      <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: "#f3f0ff", border: "1px solid #e0d7ff", display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={14} sx={{ color: "#7c3aed" }} />
        <Typography variant="caption" sx={{ color: "#7c3aed", fontStyle: "italic" }}>
          AI feedback is being generated…
        </Typography>
      </Box>
    );
  }

  if (aiFeedbackStatus === "failed" || !aiq) return null;

  const aiIsCorrect = aiq.is_correct;
  const partialScore = aiq.partial_score;
  const isAiPartial = partialScore != null && partialScore > 0 && partialScore < 1;

  const aiConflictsWithRuleCorrect = ruleIsCorrect === true && aiIsCorrect !== true;
  const aiConflictsWithRulePartial = ruleIsPartial === true && aiIsCorrect === false && !isAiPartial;
  const anyConflict = aiConflictsWithRuleCorrect || aiConflictsWithRulePartial;

  let effectiveCorrect = aiIsCorrect;
  let effectivePartial = isAiPartial;

  if (aiConflictsWithRuleCorrect) {
    effectiveCorrect = true;
    effectivePartial = false;
  } else if (aiConflictsWithRulePartial) {
    effectiveCorrect = false;
    effectivePartial = true;
  }

  const earnedMarks = partialScore != null
    ? Math.round((partialScore * (questionMarks ?? 1)) * 100) / 100
    : null;

  let bgcolor = "#f8f9fa"; let borderColor = "#e0e0e0";
  if (effectiveCorrect === true && !effectivePartial) { bgcolor = "#f0fdf4"; borderColor = "#bbf7d0"; }
  else if (effectivePartial) { bgcolor = "#fffbeb"; borderColor = "#fde68a"; }
  else if (effectiveCorrect === false) { bgcolor = "#fff5f5"; borderColor = "#fecaca"; }

  return (
    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor, border: "1px solid", borderColor }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75, flexWrap: "wrap" }}>
        <AiIcon sx={{ fontSize: 15, color: "#7c3aed" }} />
        <Typography variant="caption" sx={{ fontWeight: 700, color: "#7c3aed", textTransform: "uppercase", letterSpacing: 0.5 }}>
          AI Feedback
        </Typography>
        {effectiveCorrect === true && !effectivePartial && (
          <Chip icon={<CheckCircleIcon sx={{ fontSize: 13 }} />} label="Correct" size="small"
            sx={{ bgcolor: "#dcfce7", color: "#166534", fontWeight: 600, height: 20, fontSize: "0.7rem" }} />
        )}
        {effectivePartial && (
          <Chip
            label={
              aiConflictsWithRulePartial
                ? "Partially correct"
                : `Partial — ${earnedMarks != null ? earnedMarks : Math.round(partialScore * 100) + "%"} marks`
            }
            size="small"
            sx={{ bgcolor: "#fef3c7", color: "#92400e", fontWeight: 600, height: 20, fontSize: "0.7rem" }}
          />
        )}
        {effectiveCorrect === false && !effectivePartial && (
          <Chip icon={<CancelIcon sx={{ fontSize: 13 }} />} label="Incorrect" size="small"
            sx={{ bgcolor: "#fee2e2", color: "#991b1b", fontWeight: 600, height: 20, fontSize: "0.7rem" }} />
        )}
        {aiConflictsWithRuleCorrect && (
          <Chip label="Re-evaluated: Correct" size="small"
            sx={{ bgcolor: "#dcfce7", color: "#166534", fontWeight: 600, height: 20, fontSize: "0.7rem" }} />
        )}
        {aiConflictsWithRulePartial && (
          <Chip label="Re-evaluated: Partially correct" size="small"
            sx={{ bgcolor: "#fef3c7", color: "#92400e", fontWeight: 600, height: 20, fontSize: "0.7rem" }} />
        )}
      </Box>
      {aiq.feedback && (
        <Box sx={{ fontSize: "0.875rem", color: "text.primary", lineHeight: 1.6, mb: aiq.hint ? 0.75 : 0 }}>
          <HtmlWithLatex html={aiq.feedback} />
        </Box>
      )}
      {aiConflictsWithRuleCorrect && (
        <Typography variant="caption" sx={{ color: "#166534", fontStyle: "italic", display: "block", mt: 0.5 }}>
          Note: Your answer contains the correct value — it has been accepted by the scoring system.
        </Typography>
      )}
      {aiConflictsWithRulePartial && (
        <Typography variant="caption" sx={{ color: "#92400e", fontStyle: "italic", display: "block", mt: 0.5 }}>
          Note: Your answer matches one of the required values — it has been counted as partially correct.
        </Typography>
      )}
      {aiq.hint && !anyConflict && (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mt: 0.5 }}>
          <HintIcon sx={{ fontSize: 15, color: "#d97706", mt: 0.2, flexShrink: 0 }} />
          <Typography variant="caption" sx={{ color: "#92400e", fontStyle: "italic", lineHeight: 1.5 }}>
            <strong>Tip:</strong> {aiq.hint}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const SUB_LETTERS = "abcdefghijklmnopqrstuvwxyz".split("");

const parseReviewLabel = (label) => {
  const m = String(label || "").match(/^Q(\d+)([a-z])?$/i);
  if (!m) return { questionNumber: undefined, subLabel: undefined };
  return { questionNumber: Number(m[1]), subLabel: m[2] || undefined };
};

const stripEmbeddedTableHtml = (text) =>
  String(text || "").replace(/<table[\s\S]*?<\/table>/gi, "").trim();

const collectRowImages = (row) => {
  const arr = Array.isArray(row?.image_urls) ? row.image_urls.filter(Boolean) : [];
  if (arr.length) return arr;
  return row?.image_url ? [row.image_url] : [];
};

// A matching answer is a set of links, so review it link by link: the student needs to see WHICH pair
// they got wrong, not one "a → 1, b → 2" string to diff by eye. Rows come from the server
// (row.matching_review), already paired up with the expected right-hand item.
const MatchingReview = ({ rows }) => (
  <Box sx={{ mb: 1.5 }}>
    {rows.map((pair, i) => (
      <Box
        key={i}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr auto 1fr 1fr" },
          alignItems: "center",
          gap: 1,
          px: 1.25,
          py: 1,
          borderRadius: 1,
          mb: 0.75,
          border: "1px solid",
          borderColor: pair.is_correct ? "#bbf7d0" : "#fecaca",
          bgcolor: pair.is_correct ? "#f0fdf4" : "#fff5f5",
        }}
      >
        <Box sx={{ fontSize: "0.875rem", fontWeight: 600 }}><HtmlWithLatex html={String(pair.left ?? "")} /></Box>
        <Typography sx={{ color: "text.disabled", display: { xs: "none", sm: "block" } }}>→</Typography>
        <Box sx={{ fontSize: "0.875rem", color: pair.is_correct ? "success.main" : "error.main", fontWeight: 700 }}>
          {pair.your ? <HtmlWithLatex html={String(pair.your)} /> : <Typography variant="body2" color="text.disabled">— not answered</Typography>}
        </Box>
        {!pair.is_correct && (
          <Box sx={{ fontSize: "0.8rem", color: "success.dark" }}>
            <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5 }}>Correct:</Typography>
            <HtmlWithLatex html={String(pair.correct ?? "")} />
          </Box>
        )}
      </Box>
    ))}
  </Box>
);

// ── Single question review card (used standalone and as a grouped sub-question) ─
const QuestionReviewCard = ({ row, label, aiFeedbackMap, aiFeedbackStatus }) => {
  const qt = String(row.question_type || "").toLowerCase().replace(/_/g, "-");
  const isText = ["short_answer", "essay", "short answer", "text"].some((t) => qt.includes(t));
  const isTableQ = qt === "table" && row.answer_data?.table_layout?.grid?.length > 0;
  const { questionNumber, subLabel } = parseReviewLabel(label);
  const rowImages = collectRowImages(row);
  const tableSubmitted = isTableQ ? coerceFillAnswersArray(row.your_answer, parsePipeJoinedBlanks(row.correct_answer_label)) : [];
  const tableCorrect = isTableQ
    ? (
        row.answer_data?.correct_blanks?.map((b) => (Array.isArray(b) ? (b[0] ?? "") : String(b ?? "")))
        ?? parsePipeJoinedBlanks(row.correct_answer_label)
      )
    : [];
  const tableStemText = isTableQ
    ? stripEmbeddedTableHtml(
        subLabel ? extractSubCore(row.question_text || "", subLabel) : (row.question_text || "")
      )
    : "";
  const tableReviewQuestion = isTableQ
    ? {
        type: "table",
        paper_number: row.paper_number || undefined,
        question_text: tableStemText,
        answer_data: {
          ...(row.answer_data || {}),
          correct_blanks: row.answer_data?.correct_blanks
            ?? tableCorrect.map((v) => [v]),
        },
      }
    : null;

  const matchingRows = Array.isArray(row.matching_review) && row.matching_review.length > 0 ? row.matching_review : null;
  const interactiveSpec = row.answer_data?.interactive || null;
  const correctInteractive = row.correct_interactive || null;
  // An edge-drawn shape (reflect / rotate / closed polygon / "draw the parallelogram") submits the object
  // { points, edges } — not a bare array. The old Array.isArray() test rejected it, so the student's own
  // drawing silently rendered as "—" on review. Accept both shapes; keep the object whole so the canvas
  // can draw the outline from value.edges.
  const rawYourInteractive = row.your_answer_interactive;
  const yourInteractive = Array.isArray(rawYourInteractive)
    ? rawYourInteractive
    : (rawYourInteractive && Array.isArray(rawYourInteractive.points) ? rawYourInteractive : null);
  const yourInteractiveCount = Array.isArray(yourInteractive)
    ? yourInteractive.length
    : (yourInteractive?.points?.length ?? 0);
  const hasInteractive = !!(interactiveSpec || correctInteractive);
  const interactiveGridSpec = interactiveSpec || correctInteractive;

  const overlay = row.answer_data?.overlay_layout;
  const isImageFillQ = qt === "image-fill"
    && overlay?.mode !== "inline"
    && Array.isArray(overlay?.blanks)
    && overlay.blanks.length > 0;
  const cleanBlank = (v) => {
    const raw = Array.isArray(v) ? (v[0] ?? "") : (v ?? "");
    return decodeHtmlEntities(String(raw).replace(/&nbsp;/gi, " ").replace(/<[^>]*>/g, "").trim());
  };
  const imageFillCorrect = isImageFillQ
    ? overlay.blanks.map((_, i) => cleanBlank((row.answer_data?.correct_blanks || [])[i]))
    : [];
  const imageFillSubmitted = isImageFillQ
    ? coerceFillAnswersArray(row.your_answer, imageFillCorrect).map((v) => cleanBlank(v))
    : [];

  // Multi-blank fills and tables are graded positionally by the backend (blank i ↔ correct[i]).
  // The client-side isAnswerCorrect/isAnswerPartial do order-independent matching, which would
  // wrongly accept swapped / wrong-cell values — trust the backend verdict for these.
  const blankCount = row.answer_data?.correct_blanks?.length
    || row.answer_data?.blank_labels?.length
    || parsePipeJoinedBlanks(row.correct_answer_label).length;
  const isMultiBlankFill = isFillQuestionType(qt) && blankCount > 1;
  // Server verdicts are authoritative — the client matcher only fills in when the server sent
  // none (is_correct == null), so an explicit backend "incorrect" is never upgraded here.
  const ruleIsCorrect = row.is_correct == null
    ? (!isText && !isMultiBlankFill && isAnswerCorrect(row.your_answer, row.correct_answer_label))
    : !!row.is_correct;
  const ruleIsPartial = !ruleIsCorrect && (!!row.is_partial || (row.is_correct == null && !isText && !isMultiBlankFill && isAnswerPartial(row.your_answer, row.correct_answer_label)));

  const aiq = aiFeedbackMap?.[String(row.question_id)];
  const aiSaysCorrect = aiq?.is_correct;
  const aiIsPartial = aiq?.partial_score != null && aiq.partial_score > 0 && aiq.partial_score < 1;
  const effectivelyCorrect = aiq ? (aiSaysCorrect === true || ruleIsCorrect) : ruleIsCorrect;
  const effectivelyPartial = !effectivelyCorrect && (ruleIsPartial || aiIsPartial);

  // A blank submission is "not answered" — it must not be shown as Incorrect.
  const hasAnswer = (row.your_answer != null && String(row.your_answer).trim() !== "")
    || !!row.your_answer_drawing
    || !!row.your_answer_interactive;

  const borderColor = !hasAnswer
    ? "#9e9e9e"
    : isText
    ? (aiq && aiFeedbackStatus === "completed"
        ? (aiSaysCorrect ? "#4caf50" : aiIsPartial ? "#f59e0b" : "#ef5350")
        : "#9e9e9e")
    : effectivelyCorrect ? "#4caf50"
    : effectivelyPartial ? "#f59e0b"
    : "#ef5350";

  return (
    <Card
      id={`question-${row.question_id}`}
      sx={{ mb: 0, borderRadius: 2, borderLeft: "4px solid", borderLeftColor: borderColor }}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
          {!isTableQ && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>{label}</Typography>
              <Chip size="small" label={`${row.marks ?? 0} marks`} variant="outlined" />
            </>
          )}
          {!hasAnswer ? (
            <Chip icon={<HelpOutlineIcon />} label="Not answered" color="default" variant="outlined" size="small" sx={{ fontWeight: 600 }} />
          ) : isText ? (
            aiq && aiFeedbackStatus === "completed" ? (
              aiSaysCorrect
                ? <Chip icon={<CheckCircleIcon />} label="AI Correct" color="success" size="small" sx={{ fontWeight: 700 }} />
                : aiIsPartial
                  ? <Chip label="Partially Correct" size="small" sx={{ fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e" }} />
                  : <Chip icon={<CancelIcon />} label="AI Incorrect" color="error" size="small" sx={{ fontWeight: 700 }} />
            ) : (
              <Chip icon={<HelpOutlineIcon />} label="Written / manual" color="default" size="small" sx={{ fontWeight: 600 }} />
            )
          ) : effectivelyCorrect ? (
            <Chip icon={<CheckCircleIcon />} label="Correct" color="success" size="small" sx={{ fontWeight: 700 }} />
          ) : effectivelyPartial ? (
            <Chip label="Partially correct" size="small"
              sx={{ fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e" }} />
          ) : (
            <Chip icon={<CancelIcon />} label="Incorrect" color="error" size="small" sx={{ fontWeight: 700 }} />
          )}
        </Box>

        {rowImages.map((url, ui) => (
          <Box key={ui} sx={{ mb: 1 }}>
            <Box component="img" src={url} alt=""
              sx={{ maxWidth: "100%", maxHeight: 240, borderRadius: 1, border: "1px solid", borderColor: "divider" }} />
          </Box>
        ))}

        {!isTableQ && (
          <Box sx={{ mb: 1.5 }}>
            <HtmlWithLatex html={row.question_text || ""} />
          </Box>
        )}

        {isTableQ && tableReviewQuestion ? (
          <Box sx={{ mb: 1.5 }}>
            <QuestionPaperLayout
              question={tableReviewQuestion}
              questionNumber={questionNumber ?? (row.paper_number ? Number(row.paper_number) : undefined)}
              subLabel={subLabel}
              showAnswers
              studentAnswers={tableSubmitted}
              hideImage={rowImages.length > 0}
            />
          </Box>
        ) : null}

        {isImageFillQ ? (
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1.5 }}>
            <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 240 } }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                Your answer
              </Typography>
              {imageFillSubmitted.some((v) => v !== "") ? (
                <ImageOverlayFillInput
                  overlayLayout={overlay}
                  values={imageFillSubmitted}
                  revealValues={imageFillSubmitted}
                  prefilled={row.answer_data?.blank_labels}
                  readOnly
                />
              ) : (
                <Typography variant="body1" color="text.disabled">—</Typography>
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 240 } }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                {ruleIsPartial || !effectivelyCorrect ? "Full answer" : "Correct answer"}
              </Typography>
              <ImageOverlayFillInput
                overlayLayout={overlay}
                values={imageFillCorrect}
                revealValues={imageFillCorrect}
                prefilled={row.answer_data?.blank_labels}
                readOnly
              />
            </Box>
          </Box>
        ) : null}

        {!isText && !matchingRows && row.options?.length >= 2 && row.options.some((opt) => !!opt.option_text && singleValueMatches(opt.option_text, row.correct_answer_label ?? "")) && (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 1, mb: 1.5 }}>
            {row.options.map((opt, oi) => {
              const letter = ["A", "B", "C", "D", "E"][oi] ?? String(oi + 1);
              const isCorrectOpt = !!opt.option_text && singleValueMatches(opt.option_text, row.correct_answer_label ?? "");
              const isSelectedOpt = !!opt.option_text && singleValueMatches(opt.option_text, row.your_answer ?? "");
              return (
                <Box
                  key={opt.id ?? oi}
                  sx={{
                    p: 1, borderRadius: 1, border: "1px solid",
                    borderColor: isCorrectOpt ? "success.light" : isSelectedOpt ? "error.light" : "divider",
                    bgcolor: isCorrectOpt ? "#f0fdf4" : isSelectedOpt ? "#fff5f5" : "#fafafa",
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", display: "block" }}>
                    {letter}:
                  </Typography>
                  {opt.image_url && (
                    <Box sx={{ mb: 0.5 }}>
                      <img
                        src={opt.image_url}
                        alt={opt.option_text || `Option ${oi + 1}`}
                        style={{ maxWidth: "100%", objectFit: "contain", borderRadius: 4 }}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    </Box>
                  )}
                  <Box sx={{ fontSize: "0.8rem", mb: 0.25 }}>
                    <HtmlWithLatex html={opt.option_text ?? ""} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: isCorrectOpt ? "success.main" : isSelectedOpt ? "error.main" : "text.disabled",
                    }}
                  >
                    {isCorrectOpt ? "Correct" : "Incorrect"}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        <Divider sx={{ my: 1.5 }} />

        {matchingRows ? (
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.75 }}>
              Your links
            </Typography>
            <MatchingReview rows={matchingRows} />
          </Box>
        ) : null}

        {hasInteractive ? (
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1.5 }}>
            <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 240 } }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                Your shape
              </Typography>
              {yourInteractive && yourInteractiveCount > 0 && interactiveGridSpec ? (
                <InteractiveDiagramCanvas
                  spec={interactiveGridSpec}
                  imageUrl={interactiveGridSpec.image_url || row.image_url || ""}
                  readOnly
                  revealValue={yourInteractive}
                />
              ) : (
                <Typography variant="body1" color="text.disabled">—</Typography>
              )}
            </Box>
            {correctInteractive ? (
              <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 240 } }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Correct shape
                </Typography>
                <InteractiveDiagramCanvas
                  spec={correctInteractive}
                  imageUrl={correctInteractive.image_url || row.image_url || ""}
                  readOnly
                  revealValue={interactiveRevealValue(correctInteractive)}
                />
              </Box>
            ) : null}
            {row.your_answer_drawing && (
              <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 240 } }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Your working (drawing)
                </Typography>
                <Box component="img" src={row.your_answer_drawing} alt="Submitted working"
                  sx={{ maxWidth: "100%", maxHeight: 320, borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "white" }} />
              </Box>
            )}
            {row.correct_drawing && (
              <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 240 } }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Correct plotted solution
                </Typography>
                <Box component="img" src={row.correct_drawing} alt="Correct plotted solution"
                  sx={{ maxWidth: "100%", maxHeight: 320, borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "white" }} />
              </Box>
            )}
          </Box>
        ) : null}

        {!isTableQ && !isImageFillQ && !hasInteractive && !matchingRows ? (
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 160 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
              Your answer
              {ruleIsPartial && (
                <Typography component="span" variant="body2" sx={{ ml: 0.75, color: "#b45309", fontWeight: 700 }}>
                  — Partially correct
                </Typography>
              )}
            </Typography>
            {row.your_answer ? (
              <Box
                sx={{
                  color: isText ? "text.primary"
                    : effectivelyCorrect ? "success.main"
                    : effectivelyPartial ? "#b45309"
                    : "error.main",
                  fontWeight: effectivelyCorrect ? 700 : 400,
                  fontSize: "0.875rem",
                }}
              >
                <HtmlWithLatex html={stripAnswerBrackets(row.your_answer)} />
              </Box>
            ) : !row.your_answer_drawing ? (
              <Typography variant="body1" color="text.disabled">—</Typography>
            ) : null}
            {row.your_answer_drawing && (
              <Box sx={{ mt: row.your_answer ? 1 : 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
                  Your working (drawing)
                </Typography>
                <Box component="img" src={row.your_answer_drawing} alt="Submitted working"
                  sx={{ maxWidth: "100%", maxHeight: 320, borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "white" }} />
              </Box>
            )}
          </Box>

          {!isText && row.correct_answer_label && (
            <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 160 }, width: { xs: "100%", sm: "auto" } }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                {ruleIsPartial ? "Full answer" : "Correct answer"}
              </Typography>
              <Box sx={{ color: "success.dark", fontWeight: 700, fontSize: "0.875rem" }}>
                <HtmlWithLatex html={stripAnswerBrackets(row.correct_answer_label)} />
              </Box>
            </Box>
          )}
          {row.correct_drawing && (
            <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 220 }, width: { xs: "100%", sm: "auto" } }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                Correct plotted solution
              </Typography>
              <Box component="img" src={row.correct_drawing} alt="Correct plotted solution"
                sx={{ maxWidth: "100%", maxHeight: 320, borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "white" }} />
            </Box>
          )}
        </Box>
        ) : null}

        <AiFeedbackPanel
          questionId={row.question_id}
          aiFeedbackMap={aiFeedbackMap}
          aiFeedbackStatus={aiFeedbackStatus}
          questionMarks={row.marks}
          ruleIsCorrect={ruleIsCorrect}
          ruleIsPartial={ruleIsPartial}
        />

        {row.answer_explanation && (
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "#f8faff", borderRadius: 2, border: "1px solid #e8ecff" }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#1e40af", display: "block", mb: 0.5 }}>
              Explanation
            </Typography>
            <Box sx={{ fontSize: "0.875rem", lineHeight: 1.6 }}>
              <HtmlWithLatex html={row.answer_explanation} />
            </Box>
          </Box>
        )}

        {row.explanation_video_url && row.video_generation_status === "completed" && (
          <Box sx={{ mt: 2 }}>
            <VideoExplanationPlayer
              videoUrl={row.explanation_video_url}
              questionText={row.question_text || label}
            />
          </Box>
        )}
        {row.video_generation_status && row.video_generation_status !== "completed" && !row.explanation_video_url && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Explanation video: {row.video_generation_status.replace(/_/g, " ")}…
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// ── Grouped question: stem + nested sub-question review cards ──────────────────
const GroupedReviewCard = ({ row, number, aiFeedbackMap, aiFeedbackStatus }) => {
  const subs = row.sub_questions || [];
  const stemText = row.question_text || row.group_title || "";

  return (
    <Card sx={{ mb: 2, borderRadius: 2, borderLeft: "4px solid", borderLeftColor: "#7c3aed" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Q{number}</Typography>
          <Chip size="small" label={`${subs.length} part${subs.length === 1 ? "" : "s"}`} variant="outlined" />
          {row.section_title && <Chip size="small" label={row.section_title} variant="outlined" />}
        </Box>

        {collectRowImages(row).map((url, ui) => (
          <Box key={ui} sx={{ mb: 1 }}>
            <Box component="img" src={url} alt=""
              sx={{ maxWidth: "100%", maxHeight: 260, borderRadius: 1, border: "1px solid", borderColor: "divider" }} />
          </Box>
        ))}

        {stemText && (
          <Box sx={{ mb: 1.5 }}>
            <HtmlWithLatex html={stemText} />
          </Box>
        )}

        {row.section_instructions && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontStyle: "italic" }}>
            {row.section_instructions}
          </Typography>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {subs.map((sub, i) => (
            <QuestionReviewCard
              key={sub.question_id ?? i}
              row={sub}
              label={`Q${number}${SUB_LETTERS[i] ?? i + 1}`}
              aiFeedbackMap={aiFeedbackMap}
              aiFeedbackStatus={aiFeedbackStatus}
            />
          ))}
        </Box>

        {row.answer_explanation && (
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "#f8faff", borderRadius: 2, border: "1px solid #e8ecff" }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#1e40af", display: "block", mb: 0.5 }}>
              Explanation
            </Typography>
            <Box sx={{ fontSize: "0.875rem", lineHeight: 1.6 }}>
              <HtmlWithLatex html={row.answer_explanation} />
            </Box>
          </Box>
        )}

        {row.explanation_video_url && row.video_generation_status === "completed" && (
          <Box sx={{ mt: 2 }}>
            <VideoExplanationPlayer
              videoUrl={row.explanation_video_url}
              questionText={row.question_text || `Q${number}`}
            />
          </Box>
        )}
        {row.video_generation_status && row.video_generation_status !== "completed" && !row.explanation_video_url && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Explanation video: {row.video_generation_status.replace(/_/g, " ")}…
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const StudentPaperReview = () => {
  const { purchaseId } = useParams();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  const [tab, setTab] = useState("questions");
  const [aiFeedbackStatus, setAiFeedbackStatus] = useState("pending");
  const [aiFeedbackMap, setAiFeedbackMap] = useState({});
  const [aiOverall, setAiOverall] = useState(null);
  const [aiScore, setAiScore] = useState(null);
  const [aiPercentage, setAiPercentage] = useState(null);
  const pollRef = useRef(null);
  const pollCountRef = useRef(0);
  const MAX_POLLS = 20;

  useEffect(() => {
    const handleExamModeChanged = () => window.location.reload();
    window.addEventListener("exam-mode-changed", handleExamModeChanged);
    return () => window.removeEventListener("exam-mode-changed", handleExamModeChanged);
  }, []);

  const load = useCallback(async () => {
    if (!purchaseId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/${getPastPaperApiPrefix()}/paper/${purchaseId}/submission-review`);
      const data = res.data?.data ?? res.data;
      if (!data?.questions) {
        setPayload(null);
        setError("No review data returned.");
      } else {
        setPayload(data);
        if (data.ai_feedback_status) setAiFeedbackStatus(data.ai_feedback_status);
        if (data.ai_score != null) setAiScore(data.ai_score);
      }
    } catch (e) {
      const msg = e.response?.data?.message || "Could not load review.";
      setError(msg);
      toast.error(msg);
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [purchaseId]);

  const fetchAiFeedback = useCallback(async () => {
    if (!purchaseId) return;
    try {
      const res = await api.get(`/${getPastPaperApiPrefix()}/paper/${purchaseId}/ai-feedback`);
      const p = res.data?.data ?? res.data;
      const status = p?.status ?? "pending";
      setAiFeedbackStatus(status);
      if (status === "completed") {
        setAiFeedbackMap(p?.question_feedback ?? {});
        setAiOverall(p?.overall ?? null);
        setAiScore(p?.ai_score ?? null);
        setAiPercentage(p?.ai_percentage ?? null);
        clearInterval(pollRef.current);
        try {
          const r2 = await api.get(`/${getPastPaperApiPrefix()}/paper/${purchaseId}/submission-review`);
          const d = r2.data?.data ?? r2.data;
          setPayload(d);
        } catch {
          // non-critical
        }
      } else if (status === "failed") {
        clearInterval(pollRef.current);
      }
    } catch {
      // silently ignore polling errors
    }
  }, [purchaseId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!purchaseId) return;
    fetchAiFeedback();
    pollRef.current = setInterval(() => {
      pollCountRef.current += 1;
      if (pollCountRef.current >= MAX_POLLS) { clearInterval(pollRef.current); return; }
      if (aiFeedbackStatus === "completed" || aiFeedbackStatus === "failed") { clearInterval(pollRef.current); return; }
      fetchAiFeedback();
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [purchaseId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (aiFeedbackStatus === "completed" || aiFeedbackStatus === "failed") {
      clearInterval(pollRef.current);
    }
  }, [aiFeedbackStatus]);

  useEffect(() => {
    if (!loading && payload && hash) {
      const id = hash.replace(/^#/, "");
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [loading, payload, hash]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error && !payload) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} variant="contained" onClick={() => navigate(getPastPaperPortalHomePath())}>
          Back to Past Papers
        </Button>
      </Box>
    );
  }

  const questions = payload?.questions || [];
  // The AI score refines the deterministic score, it never lowers it — an AI score of 0 is `!= null`, so
  // taking it verbatim let a failed/under-reporting AI wipe marks the rule grader had confirmed.
  const ruleScoreNum = Number(payload?.score ?? 0);
  const displayScore = aiScore != null ? Math.max(Number(aiScore), ruleScoreNum) : ruleScoreNum;
  const displayPct = (payload?.total_marks ?? 0) > 0
    ? Math.round((displayScore / payload.total_marks) * 1000) / 10
    : Number(payload?.percentage ?? 0);

  const computeLeafMarks = (row) => {
    const qt = String(row.question_type || "").toLowerCase().replace(/_/g, "-");
    const isText = ["short_answer", "essay", "short answer", "text"].some((t) => qt.includes(t));
    const marks = Number(row.marks ?? 0);
    const blankCount = row.answer_data?.correct_blanks?.length
      || row.answer_data?.blank_labels?.length
      || parsePipeJoinedBlanks(row.correct_answer_label).length;
    const isMultiBlankFill = isFillQuestionType(qt) && blankCount > 1;
    // Server verdicts are authoritative — client matching only when the server sent none.
    const ruleIsCorrect = row.is_correct == null
      ? (!isText && !isMultiBlankFill && isAnswerCorrect(row.your_answer, row.correct_answer_label))
      : !!row.is_correct;
    const ruleIsPartial = !ruleIsCorrect && (!!row.is_partial || (row.is_correct == null && !isText && !isMultiBlankFill && isAnswerPartial(row.your_answer, row.correct_answer_label)));
    const aiq = aiFeedbackMap?.[String(row.question_id)];
    const aiIsPartial = aiq?.partial_score != null && aiq.partial_score > 0 && aiq.partial_score < 1;
    const effectivelyCorrect = aiq ? (aiq.is_correct === true || ruleIsCorrect) : ruleIsCorrect;
    const effectivelyPartial = !effectivelyCorrect && (ruleIsPartial || aiIsPartial);
    const hasAnswer = (row.your_answer != null && row.your_answer !== "") || !!row.your_answer_drawing || !!row.your_answer_interactive;

    let result;
    let earned = 0;
    if (effectivelyCorrect) {
      result = "correct";
      earned = marks;
    } else if (effectivelyPartial) {
      result = "partial";
      earned = aiq?.partial_score != null && aiq.partial_score > 0
        ? Math.round(aiq.partial_score * marks * 100) / 100
        : Math.round(marks * 0.5 * 100) / 100;
    } else if (!hasAnswer) {
      result = "unanswered";
    } else if (isText && !(aiq && aiFeedbackStatus === "completed")) {
      result = "written";
    } else {
      result = "incorrect";
    }
    return { result, earned, max: marks };
  };

  const breakdownRows = [];
  questions.forEach((row, idx) => {
    const isGrouped = String(row.type || row.question_type || "").toLowerCase() === "grouped"
      || (Array.isArray(row.sub_questions) && row.sub_questions.length > 0);
    const mainNum = idx + 1;
    if (isGrouped) {
      (row.sub_questions || []).forEach((sub, si) => {
        const m = computeLeafMarks(sub);
        breakdownRows.push({
          key: sub.question_id ?? `${mainNum}-${si}`,
          qLabel: si === 0 ? `Q${mainNum}` : "",
          partLabel: (SUB_LETTERS[si] ?? String(si + 1)).toUpperCase(),
          questionText: sub.question_text,
          ...m,
          isMainRow: si === 0,
        });
      });
    } else {
      const m = computeLeafMarks(row);
      breakdownRows.push({
        key: row.question_id ?? idx,
        qLabel: `Q${row.display_label ?? mainNum}`,
        partLabel: "",
        questionText: row.question_text,
        ...m,
        isMainRow: true,
      });
    }
  });

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Button size="small" startIcon={<ArrowBackIcon />} sx={{ mb: 2 }}
        onClick={() => navigate(getPastPaperResultPath(purchaseId, 'result'))}>
        Back to Result
      </Button>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {payload?.paper_name || "Paper review"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Question-by-question review
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            {aiScore != null ? (
              <>
                <Tooltip title="AI-adjusted score (semantic matching)">
                  <Chip
                    icon={<AiIcon sx={{ fontSize: 16 }} />}
                    label={`AI Score: ${Number(displayScore).toFixed(1)} / ${Number(payload?.total_marks ?? 0).toFixed(1)}`}
                    color="secondary" sx={{ fontWeight: 700 }}
                  />
                </Tooltip>
                <Chip label={`${Number(displayPct).toFixed(1)}%`} variant="outlined" color="secondary" />
              </>
            ) : (
              <>
                <Chip label={`Score: ${Number(displayScore).toFixed(1)} / ${Number(payload?.total_marks ?? 0).toFixed(1)}`} color="primary" />
                <Chip label={`${Number(displayPct).toFixed(1)}%`} variant="outlined" />
              </>
            )}
            {(aiFeedbackStatus === "pending" || aiFeedbackStatus === "processing") && (
              <Chip
                icon={<CircularProgress size={12} sx={{ color: "#7c3aed" }} />}
                label="AI feedback generating…" size="small"
                sx={{ bgcolor: "#f3f0ff", color: "#7c3aed", border: "1px solid #e0d7ff" }}
              />
            )}
            {aiFeedbackStatus === "completed" && (
              <Chip icon={<AiIcon sx={{ fontSize: 14 }} />} label="AI feedback ready" size="small"
                sx={{ bgcolor: "#f3f0ff", color: "#7c3aed", border: "1px solid #e0d7ff" }} />
            )}
          </Box>

        </CardContent>
      </Card>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 3 }}>
        <Tab value="questions" label="Questions" />
        <Tab value="marks" label="Marks Breakdown" />
        <Tab value="ai" label={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <AiIcon sx={{ fontSize: 16 }} />
            AI Insights
            {(aiFeedbackStatus === "pending" || aiFeedbackStatus === "processing") && (
              <CircularProgress size={10} sx={{ ml: 0.5, color: "#7c3aed" }} />
            )}
          </Box>
        } />
      </Tabs>

      {tab === "questions" && (
        <>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Questions {questions.length > 0 ? `(Total: ${payload?.top_level_count ?? questions.length})` : ""}
          </Typography>

          {questions.length === 0 ? (
            <Alert severity="info">No questions in this review.</Alert>
          ) : (
            questions.map((row, idx) => {
              const isGrouped = String(row.type || row.question_type || "").toLowerCase() === "grouped"
                || (Array.isArray(row.sub_questions) && row.sub_questions.length > 0);
              return isGrouped ? (
                <GroupedReviewCard
                  key={row.question_id ?? idx}
                  row={row}
                  number={idx + 1}
                  aiFeedbackMap={aiFeedbackMap}
                  aiFeedbackStatus={aiFeedbackStatus}
                />
              ) : (
                <QuestionReviewCard
                  key={row.question_id ?? idx}
                  row={row}
                  label={`Q${row.display_label ?? idx + 1}`}
                  aiFeedbackMap={aiFeedbackMap}
                  aiFeedbackStatus={aiFeedbackStatus}
                />
              );
            })
          )}
        </>
      )}

      {tab === "marks" && (
        <MarksBreakdownTable
          rows={breakdownRows}
          totalMarks={payload?.total_marks ?? 0}
          scoredMarks={displayScore}
          title="Marks Breakdown"
          fileName={`${(payload?.paper_name || "paper").replace(/[^\w-]+/g, "_").slice(0, 60)}-marks`}
        />
      )}

      {tab === "ai" && (
        <AiInsightsTab
          aiFeedbackStatus={aiFeedbackStatus}
          aiOverall={aiOverall}
          aiScore={aiScore}
          aiPercentage={aiPercentage}
          totalMarks={payload?.total_marks}
          ruleScore={payload?.score}
        />
      )}
    </Box>
  );
};

export default StudentPaperReview;
